import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { hasDotSegment } from '../../hooks/useProxyAdmin';
import { SegmentedControl } from '../SegmentedControl';

export type View = 'active' | 'inactive' | 'all';
const VIEWS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
] as const;

/**
 * The row-actions column: pinned to the table's right edge, so a row's actions
 * stay in reach while the table scrolls sideways. Its shadow marks the seam,
 * only while the table is wider than its box.
 */
export const ACTIONS_CELL =
  'sticky right-0 whitespace-nowrap bg-surface pl-4 pr-2 text-right group-data-[overflowing]/scroller:shadow-[-12px_0_10px_-10px_rgb(0_0_0/0.22)]';
/**
 * A body row's top rule, drawn by each cell: the tables don't collapse borders,
 * because a sticky cell's background paints over a collapsed row border.
 */
export const ROW_RULE = '[&>td]:border-t [&>td]:border-line';
/** Tables keep separate borders, so a pinned cell carries its own rule. */
export const TABLE =
  'w-full border-separate border-spacing-0 whitespace-nowrap text-left';

/**
 * Filters on the left, the section's actions on the right. Always the same
 * height, so the list below doesn't jump when it empties.
 */
export function Toolbar({
  view,
  onView,
  query,
  onQuery,
  noun,
  count,
  children,
}: {
  view: View;
  onView: (view: View) => void;
  query: string;
  onQuery: (query: string) => void;
  /** Singular, as in "Find a …". */
  noun: string;
  /** Once loaded: how many match the search, of how many listed. */
  count: { shown: number; total: number } | undefined;
  children: ReactNode;
}) {
  const searchId = useId();
  const plural = (n: number) =>
    `${n.toLocaleString()} ${noun}${n === 1 ? '' : 's'}`;
  return (
    <div className="mt-3 flex min-h-8 flex-wrap items-center gap-x-3 gap-y-2">
      <SegmentedControl
        label={`${noun} status`}
        options={VIEWS}
        value={view}
        onChange={onView}
      />
      <label htmlFor={searchId} className="sr-only">
        Find a {noun}
      </label>
      <input
        id={searchId}
        className="input h-7 w-56 max-w-full text-xs"
        type="search"
        placeholder={`Find a ${noun}`}
        value={query}
        onChange={e => onQuery(e.target.value)}
      />
      {count && (
        <span className="tabular-nums text-muted">
          {count.shown === count.total
            ? plural(count.total)
            : `${count.shown.toLocaleString()} of ${plural(count.total)}`}
        </span>
      )}
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {children}
      </div>
    </div>
  );
}

const ICON = {
  width: 14,
  height: 14,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/**
 * A row's Edit and Deactivate/Activate, as icon buttons. Named by aria-label
 * (with the row's name, since a screen reader hears it out of the table) and
 * explained by a native `title`: the `.tip` tooltip would be clipped by the
 * table's scroll box.
 */
export function RowActions({
  name,
  active,
  busy,
  onEdit,
  onToggle,
}: {
  name: string;
  active: boolean;
  busy: boolean;
  onEdit: () => void;
  onToggle: () => void;
}) {
  const toggle = active ? 'Deactivate' : 'Activate';
  // No URL can address it (see hasDotSegment); say why rather than misfire.
  const blocked = hasDotSegment(name)
    ? "Can't be managed here: a '.' or '..' segment in the name can't be sent in a URL. Use `hawk proxy`."
    : null;
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        className="btn btn-sm btn-icon bg-transparent text-muted hover:text-ink"
        title={blocked ?? 'Edit'}
        aria-label={`Edit ${name}`}
        disabled={busy || blocked !== null}
        onClick={onEdit}
      >
        <svg {...ICON}>
          <path d="M11 2.75 13.25 5 6 12.25l-3 .75.75-3z" />
          <path d="M9.5 4.25 11.75 6.5" />
        </svg>
      </button>
      <button
        type="button"
        className={`btn btn-sm btn-icon bg-transparent ${active ? 'text-muted hover:text-err' : 'text-accent'}`}
        title={blocked ?? toggle}
        aria-label={`${toggle} ${name}`}
        disabled={busy || blocked !== null}
        onClick={onToggle}
      >
        {active ? (
          // A slashed circle: stop serving it.
          <svg {...ICON}>
            <circle cx="8" cy="8" r="5.75" />
            <path d="M3.95 3.95 12.05 12.05" />
          </svg>
        ) : (
          // Power: serve it again.
          <svg {...ICON}>
            <path d="M8 2.25v5.25" />
            <path d="M4.75 4.5a5 5 0 1 0 6.5 0" />
          </svg>
        )}
      </button>
    </span>
  );
}

/** A table that scrolls sideways in its own box instead of the page. */
export function TableScroller({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  // Re-measured as the box or the table resizes (a window resize, rows opening).
  useEffect(() => {
    const box = ref.current;
    if (!box || typeof ResizeObserver === 'undefined') return;
    const measure = () => setOverflowing(box.scrollWidth > box.clientWidth);
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    if (box.firstElementChild) observer.observe(box.firstElementChild);
    measure();
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      data-overflowing={overflowing || undefined}
      className="group/scroller mt-1 overflow-x-auto"
    >
      {children}
    </div>
  );
}
